import AppButton from '@nxsys/components/AppButton';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppIconButton from '@nxsys/components/AppIconButton';
import AppInput from '@nxsys/components/AppInput';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { Icons } from '@nxsys/types/icons';
import { IClientApplication } from '@nxsys/types/models/clientApplication';
import { Form } from 'antd';
import { FormProps } from 'antd/lib';
import React, { Ref, useEffect, useImperativeHandle, useState } from 'react';

type ClientApplicationFormProps = {
  formRef?: Ref<ClientApplicationFormRef>;
  onSubmit?: (values: ClientApplicationValues) => Promise<void>;
  data?: IClientApplication;
};

export type ClientApplicationFormRef = {
  submit: () => void;
  reset: () => void;
};

export type ClientApplicationValues = {
  clientName: string;
  redirectUris: string[];
};

const ClientApplicationForm: React.FC<ClientApplicationFormProps> = ({
  formRef,
  onSubmit,
  data,
}) => {
  const { messages } = useTypedIntl();
  const [redirectUris, setRedirectUris] = useState<string[]>(['']);
  const [needReinitialize, setNeedReinitialize] = useState(true);

  const [form] = Form.useForm<ClientApplicationValues>();

  useImperativeHandle(formRef, () => ({
    submit() {
      form.submit();
    },
    reset() {
      form.resetFields();
      setRedirectUris(['']);
    },
  }));

  useEffect(() => {
    if (data && needReinitialize) {
      form.setFieldsValue({
        clientName: data.clientName || '',
      });
      setRedirectUris(data.redirectUris || ['']);
      setNeedReinitialize(false);
    }
  }, [data]);

  const onFinish: FormProps['onFinish'] = async (values) => {
    await onSubmit?.({
      clientName: values.clientName || '',
      redirectUris: redirectUris,
    });
  };

  const onFinishFailed: FormProps['onFinishFailed'] = (errorInfo) => {
    console.log(errorInfo);
  };

  return (
    <AppFormContainer
      form={form}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      layout="vertical"
      labelCol={{ span: 24 }}
      wrapperCol={{ span: 24 }}
    >
      <Form.Item
        label={messages['company.clientApplication.clientName']}
        name="clientName"
        rules={[{ required: true, message: messages['form.pleaseEnter'] }]}
      >
        <AppInput placeholder={messages['company.clientApplication.enter']} />
      </Form.Item>
      {redirectUris?.map((uri, index) => (
          <Form.Item
            key={index}
            name={`redirectUris[${index}]`}
            label={
              index === 0
                ? messages['company.clientApplication.redirectUrls']
                : ' '
            }
            rules={[
              {
                type: 'url',
                message: messages['company.clientApplication.validUrl'],
              },
            ]}
          >
          <div className="flex items-center gap-[8px]">
            <AppInput
              placeholder={messages['company.clientApplication.enter']}
              value={uri}
              onChange={(e) =>
                setRedirectUris(
                  redirectUris.map((uri, i) =>
                    i === index ? e.target.value : uri,
                  ),
                )
              }
              allowCapitalize={false}
            />
            <AppIconButton
              icon={<Icons.IconDelete />}
              title={messages['company.clientApplication.delete']}
              disabled={redirectUris.length === 1}
              onClick={() => {
                setRedirectUris(redirectUris.filter((_, i) => i !== index));
              }}
            />
          </div>
        </Form.Item>
      ))}
      <AppButton
        variant="outlined"
        name={messages['company.clientApplication.addNew']}
        onClick={() => {
          setRedirectUris([...redirectUris, '']);
        }}
        leftIcon={<Icons.IconPlus />}
      />
    </AppFormContainer>
  );
};

export default ClientApplicationForm;
